import React, { useState, useRef, useEffect, useMemo } from 'react';

import { Input, List } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { useContact } from '@hooks/API/Contact/UseContact';
import { useModelsList } from '@hooks/API/Contact/UseContactsList';
import { useScoutingMeeting, forceReloadScoutingMeeting } from '@hooks/API/Event/UseScoutingMeeting';
import { forceReloadTravelPlan } from '@hooks/API/UseTravelPlan';

import CreateModelForm from '@components/ContactCreator/CreateModelForm';
import MeetingModelMedia from '@components/EventDrawer/ScoutingMeetingsEvent/MeetingModelMedia';
import { Features } from '@components/Features/FeaturesDrawer';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { NwAddButton, NwButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { ModelCover } from '@components/ModelCover';

const StyledInput = styled(Input)`
    width: 450px;
`;

const Container = styled.div`
    display: flex;

    .col {
        flex: 1;
        padding: 0 1em 1em;
    }

    .model-list {
        border-right: 1px solid ${(props) => props.theme.LighterBorderColor};
    }

    .ant-list-item {
        cursor: pointer;
    }
`;

const AddMeetingModelDrawer = ({ travelPlanId, meetingId, selectedModels, onClose }) => {
    const [filter, setFilter] = useState('');
    const [isFiltered, setFiltered] = useState(false);
    const { data: fullModelList } = useModelsList();
    const [isCreateModel, setCreateModel] = useState(false);
    const searchRef = useRef(null);
    const [createdModelId, setCreatedModelId] = useState();
    const [modelCreationStep, setModelCreationStep] = useState('creation');
    const { data: scoutingMeetingsData } = useScoutingMeeting(meetingId);
    const [touchForm, setTouchForm] = useState(false);
    // Features vuole il contatto intero (ID, Departments, ContactType), non il solo id:
    // finché createdModelId è vuoto la query resta disabilitata
    const { data: createdModel } = useContact(createdModelId);

    useEffect(() => {
        if (searchRef.current) {
            searchRef.current.focus();
        }

    }, [searchRef.current]);

    const handleSearch = (event) => {
        if (!isFiltered) {
            setFiltered(true);
        }
        setFilter(event.target.value);
    };

    const handleCreateModel = () => {
        setCreateModel(true);
    };

    const handleAddModel = async (modelId) => {
        const modelViewData = {
            MeetingID: meetingId,
            ModelID: modelId,
        };

        try {
            await Axios.post('/ScoutingMeetings/AddModelView', modelViewData);
            await forceReloadScoutingMeeting(meetingId);
            await forceReloadTravelPlan(travelPlanId);
        } catch (error) {
            console.error('Add Model View Error => ', error);
        }
    };

    const handleCreatedModel = (modelId) => {
        handleAddModel(modelId);
        setModelCreationStep('feature');
        setCreatedModelId(modelId);
    };

    const renderCreationModel = () => {
        switch (modelCreationStep) {
            case 'creation':
                let filters = filter.split(' ');
                const name = filters[0];
                filters.splice(0, 1);
                const surname = filters.length > 0 ? filters.join(' ') : '';
                return (
                    <CreateModelForm
                        defaultFormData={{
                            InTown: false,
                            Name: name,
                            Surname: surname,
                            MotherAgencyID: scoutingMeetingsData.ContactID,
                        }}
                        onModelCreated={handleCreatedModel}
                        onTouchForm={setTouchForm}
                    />
                );
            case 'feature':
                return createdModelId ? (
                    <>
                        <FlexContainer justifyContent="flex-end" mg="0 0 16px">
                            <NwButton primary label="Next" onClick={() => setModelCreationStep('media')} />
                        </FlexContainer>
                        {createdModel ? <Features currentContact={createdModel} /> : <Loading textBlack small />}
                    </>
                ) : (
                    <div />
                );
            case 'media':
                return (
                    <MeetingModelMedia
                        modelId={createdModelId}
                        meetingId={meetingId}
                        onClose={() => {
                            forceReloadScoutingMeeting(meetingId);
                            onClose();
                        }}
                    />
                );
            default:
        }
    };

    const models = useMemo(() => {
        let models = !isFiltered
            ? fullModelList.filter((model) => model.ST === 5 && model.MAID === scoutingMeetingsData.ContactID)
            : filter
              ? fullModelList.filter((model) =>
                    `${model.N} ${model.S}`.toLowerCase().includes(filter.toLocaleLowerCase())
                )
              : [];
        models = models.filter((model) => !selectedModels.find((item) => item === model.ID));
        return models;

    }, [fullModelList, filter, scoutingMeetingsData]);

    return (
        <NwDrawer touchForm={touchForm} onTouchForm={setTouchForm} title={'Add Model'} onClose={onClose}>
            {isCreateModel ? (
                renderCreationModel()
            ) : (
                <>
                    <FlexContainer mg={'0 0 16px'}>
                        <StyledInput
                            autoComplete="off"
                            value={filter}
                            placeholder="Search Model"
                            onChange={handleSearch}
                            ref={searchRef}
                        />
                    </FlexContainer>
                    <Container>
                        <div className="col model-list">
                            <List
                                itemLayout="horizontal"
                                dataSource={models}
                                renderItem={(modelObject) => {
                                    return (
                                        <List.Item
                                            className="ant-list-item-top"
                                            onClick={() => {
                                                handleAddModel(modelObject.ID);
                                                onClose();
                                            }}
                                        >
                                            <List.Item.Meta
                                                avatar={<ModelCover model={modelObject} classN="model-cover-small" />}
                                                title={`${modelObject.N} ${modelObject.S}`}
                                            ></List.Item.Meta>
                                        </List.Item>
                                    );
                                }}
                            />
                        </div>
                        <div className="col">
                            <NwAddButton primary label="Create Model" onClick={handleCreateModel} />
                        </div>
                    </Container>
                </>
            )}
        </NwDrawer>
    );
};

export default AddMeetingModelDrawer;
